import React, { useState, useEffect, useMemo } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { getClientsList, getBranches } from '../utilities/api';
import { isSuperAdmin, getOwnClientId } from './notificationScope';
import { STATUS_OPTIONS, toDirectDownloadUrl } from './apkStatus';
import '../masters/CategoryMaster.css';
import './Mobilebillingapp.css';

const URL_PATTERN = /^https?:\/\/\S+$/i;

const MobilebillingappDialog = ({ visible, apkData, onHide, onSave }) => {
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);

    const superAdmin = isSuperAdmin();
    const isEdit = !!apkData?.apkid;

    useEffect(() => {
        if (!visible) return undefined;
        let cancelled = false;
        const ownClientId = getOwnClientId();
        getClientsList()
            .then((rows) => {
                if (cancelled) return;
                setClients(rows.filter((c) => superAdmin || String(c.client_id) === ownClientId).map((c) => ({ label: c.client_name, value: String(c.client_id) })));
            })
            .catch(() => !cancelled && setClients([]));
        return () => {
            cancelled = true;
        };
    }, [visible, superAdmin]);

    useEffect(() => {
        if (!visible) return;
        setErrors({});
        if (apkData) {
            setForm({
                apkid: apkData.apkid,
                client_id: apkData.client_id != null ? String(apkData.client_id) : null,
                branch_id: apkData.branch_id != null ? String(apkData.branch_id) : null,
                app_version: apkData.app_version || '',
                play_storelink: apkData.play_storelink || '',
                googledrive_link: apkData.googledrive_link || '',
                status: apkData.status || 'Active'
            });
        } else {
            setForm({
                client_id: superAdmin ? null : getOwnClientId() || null,
                branch_id: null,
                app_version: '',
                play_storelink: '',
                googledrive_link: '',
                status: 'Active'
            });
        }
    }, [visible, apkData, superAdmin]);

    // Branches follow the selected client — they belong to one client each.
    useEffect(() => {
        if (!visible || !form.client_id) {
            setBranches([]);
            return undefined;
        }
        let cancelled = false;
        getBranches(form.client_id)
            .then((rows) => {
                if (cancelled) return;
                setBranches(rows.map((b) => ({ label: b.branch_name, value: String(b.branch_id) })));
            })
            .catch(() => !cancelled && setBranches([]));
        return () => {
            cancelled = true;
        };
    }, [visible, form.client_id]);

    const update = (field, val) => {
        setForm((prev) => ({ ...prev, [field]: val }));
        if (errors[field])
            setErrors((prev) => {
                const e = { ...prev };
                delete e[field];
                return e;
            });
    };

    // Shows what the Drive link will actually resolve to, so a wrong link (a folder, a
    // viewer URL that can't be rewritten) is visible here rather than after a failed share.
    const resolvedDownload = useMemo(() => toDirectDownloadUrl(form.googledrive_link), [form.googledrive_link]);

    const validate = () => {
        const errs = {};
        if (!form.client_id) errs.client_id = 'Select a client';

        const play = String(form.play_storelink || '').trim();
        const drive = String(form.googledrive_link || '').trim();
        if (!play && !drive) {
            errs.play_storelink = 'Enter at least one link — Play Store or Google Drive';
            errs.googledrive_link = 'Enter at least one link — Play Store or Google Drive';
        }
        if (play && !URL_PATTERN.test(play)) errs.play_storelink = 'Must be a full URL starting with http:// or https://';
        if (drive && !URL_PATTERN.test(drive)) errs.googledrive_link = 'Must be a full URL starting with http:// or https://';

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        onSave({
            apkid: form.apkid || null,
            client_id: form.client_id,
            // Empty means the entry applies to every branch of this client.
            branch_id: form.branch_id || null,
            app_version: String(form.app_version || '').trim(),
            play_storelink: String(form.play_storelink || '').trim(),
            googledrive_link: String(form.googledrive_link || '').trim(),
            status: form.status || 'Active',
            acting_user: localStorage.getItem('username') || localStorage.getItem('userid') || ''
        });
    };

    const dialogHeader = (
        <div className="sub-dialog-header">
            <span className="sub-dialog-header-icon">
                <i className="pi pi-mobile" />
            </span>
            <div>
                <div className="sub-dialog-header-text">{isEdit ? 'Edit App Entry' : 'Add App Entry'}</div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
                    {isEdit ? 'Update the app links below' : 'Register a mobile billing app build for a client'}
                </div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="sub-dialog-cancel" onClick={onHide} />
            <Button label={isEdit ? 'Update' : 'Save'} icon="pi pi-check" className="sub-dialog-save" onClick={handleSave} />
        </div>
    );

    return (
        <Dialog
            header={dialogHeader}
            visible={visible}
            style={{ width: '620px' }}
            breakpoints={{ '680px': '94vw' }}
            modal
            position="center"
            footer={dialogFooter}
            onHide={onHide}
            draggable={false}
            resizable={false}
            className="sub-master-dialog mba-dialog p-fluid"
        >
            <div className="sub-form-grid">
                {/* Client */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-briefcase sub-field-label-icon" />
                        Client <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <Dropdown
                        value={form.client_id || null}
                        options={clients}
                        onChange={(e) => {
                            // A branch of the previous client would be saved against the wrong one.
                            setForm((prev) => ({ ...prev, client_id: e.value, branch_id: null }));
                            setErrors((prev) => {
                                const err = { ...prev };
                                delete err.client_id;
                                return err;
                            });
                        }}
                        placeholder="Select client"
                        filter={superAdmin}
                        disabled={!superAdmin}
                        className={`w-full${errors.client_id ? ' p-invalid' : ''}`}
                    />
                    {errors.client_id && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.client_id}
                        </span>
                    )}
                </div>

                {/* Branch */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-building sub-field-label-icon" />
                        Branch
                    </label>
                    <Dropdown
                        value={form.branch_id || null}
                        options={branches}
                        onChange={(e) => update('branch_id', e.value)}
                        placeholder="All Branches"
                        filter
                        showClear
                        disabled={!form.client_id}
                        className="w-full"
                    />
                </div>

                {/* Play Store link — full width: URLs need the room */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-google sub-field-label-icon" />
                        Play Store Link
                    </label>
                    <InputText
                        value={form.play_storelink || ''}
                        onChange={(e) => update('play_storelink', e.target.value)}
                        placeholder="https://play.google.com/store/apps/details?id=…"
                        className={`w-full${errors.play_storelink ? ' p-invalid' : ''}`}
                    />
                    {errors.play_storelink && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.play_storelink}
                        </span>
                    )}
                </div>

                {/* Google Drive link */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-cloud sub-field-label-icon" />
                        Google Drive Link (APK backup)
                    </label>
                    <InputText
                        value={form.googledrive_link || ''}
                        onChange={(e) => update('googledrive_link', e.target.value)}
                        placeholder="https://drive.google.com/file/d/…/view?usp=sharing"
                        className={`w-full${errors.googledrive_link ? ' p-invalid' : ''}`}
                    />
                    {errors.googledrive_link && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.googledrive_link}
                        </span>
                    )}
                </div>

                {/* Version */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-tag sub-field-label-icon" />
                        App Version
                    </label>
                    <InputText value={form.app_version || ''} onChange={(e) => update('app_version', e.target.value)} placeholder="e.g. 2.4.1" className="w-full" />
                </div>

                {/* Status */}
                <div className="sub-field-group">
                    <label className="sub-field-label">
                        <i className="pi pi-circle sub-field-label-icon" />
                        Status
                    </label>
                    <Dropdown value={form.status || 'Active'} options={STATUS_OPTIONS} onChange={(e) => update('status', e.value)} className="w-full" />
                </div>

                {resolvedDownload && (
                    <div className="sub-field-group sub-field-full">
                        <span className="mba-dialog-summary">
                            <i className="pi pi-info-circle" style={{ marginTop: 2 }} />
                            <span>
                                Direct download resolves to: {resolvedDownload}
                                {resolvedDownload === String(form.googledrive_link || '').trim() && ' — no Drive file id found, the link will be opened as-is.'}
                            </span>
                        </span>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default MobilebillingappDialog;
